import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import { TruncatedTextPopover } from '@ardoq/popovers';
import { s8 } from '@ardoq/design-tokens';
import {
  containsImage,
  MarkdownViewer,
  markdownToTextPreview,
} from '@ardoq/markdown';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { type I18nLocale, t } from '@ardoq/i18n';
import { text1, text2 } from '@ardoq/typography';

export const DEFAULT_MAX_TEXT_LENGTH = 180;

export const FlexWrapper = styled.span`
  display: flex;
  align-items: center;
`;

export const LabelWrapper = styled.span<{ $upperCase?: boolean }>`
  margin-left: 8px;
  ${props =>
    props.$upperCase &&
    css`
      text-transform: uppercase;
    `}
`;

export const Spacer = styled.span`
  width: ${s8};
`;

const RICH_POPOVER_WIDTH = '454px';
/** Keeps the popover clear of the viewport edges on a narrow screen. */
const VIEWPORT_GUTTER = '32px';

/**
 * Image widths are a share of their container, so the container needs a width
 * that does not depend on its own content. Without one the popover is sized by
 * the text, and an image grows with the length of the description beside it —
 * or collapses when there is no text at all. The width still yields to a
 * viewport narrower than the popover.
 */
const RichPopoverContent = styled.div<{ $hasImage: boolean }>`
  ${({ $hasImage }) =>
    $hasImage &&
    `width: min(${RICH_POPOVER_WIDTH}, calc(100vw - ${VIEWPORT_GUTTER}));`}
  max-width: min(${RICH_POPOVER_WIDTH}, calc(100vw - ${VIEWPORT_GUTTER}));
  max-height: 544px;
  overflow: auto;
`;

const TruncatedMarkdown = styled(TruncatedTextPopover)<{
  $variant: 'light' | 'default';
}>`
  ${({ $variant }) => ($variant === 'light' ? text2 : text1)};
`;

const ImageIndicator = styled(Icon)`
  margin-right: ${s8};
  vertical-align: text-bottom;
`;

/** Names the hover target; the icon itself is aria-hidden inside Icon. */
const HiddenLabel = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
`;

export const MarkdownInPopover = ({
  value,
  variant = 'default',
  i18nLocale,
}: {
  value: string;
  variant?: 'light' | 'default';
  i18nLocale: I18nLocale;
}) => {
  // Both parse markdown, and this renders once per grid cell.
  const { parsedPreview, hasImage } = useMemo(
    () => ({
      parsedPreview: markdownToTextPreview(value),
      // The preview is plain text, so an image leaves no trace in the cell and
      // the popover would not open for a description that is only an image.
      hasImage: containsImage(value),
    }),
    [value]
  );

  return (
    <TruncatedMarkdown
      $variant={variant}
      hasHiddenContent={hasImage}
      prefix={
        hasImage ? (
          <>
            <ImageIndicator
              iconName={IconName.COLLECTIONS}
              iconSize={IconSize.SMALL}
            />
            <HiddenLabel>
              {t(
                i18nLocale,
                'renderers:markdown.containsImage',
                'Contains an image'
              )}
            </HiddenLabel>
          </>
        ) : undefined
      }
      content={() => (
        <RichPopoverContent $hasImage={hasImage}>
          <MarkdownViewer content={value} sanitationLevel="permissive" />
        </RichPopoverContent>
      )}
    >
      {parsedPreview}
    </TruncatedMarkdown>
  );
};
